import { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    AppBar,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Menu,
    MenuItem,
    Button,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/Dashboard'
import PeopleIcon from '@mui/icons-material/People'
import DescriptionIcon from '@mui/icons-material/Description'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import BusinessIcon from '@mui/icons-material/Business'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { useAuth } from '../contexts/AuthContext'

const drawerWidth = 240

interface LayoutProps {
    children: ReactNode
}

const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Applications', icon: <DescriptionIcon />, path: '/applications' },
    { text: 'Broker Admin', icon: <BusinessIcon />, path: '/brokers' },
    { text: 'API Traffic', icon: <CompareArrowsIcon />, path: '/api-traffic' },
]

export default function Layout({ children }: LayoutProps) {
    const [mobileOpen, setMobileOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const navigate = useNavigate()
    const { user, logout } = useAuth()

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen)
    }

    const handleNavigation = (path: string) => {
        navigate(path)
        setMobileOpen(false)
    }

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleUserMenuClose = () => {
        setAnchorEl(null)
    }

    const handleChangePassword = () => {
        handleUserMenuClose()
        navigate('/change-password')
    }

    const handleLogout = () => {
        handleUserMenuClose()
        logout()
        navigate('/login')
    }

    const drawer = (
        <div>
            <Toolbar>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <img src="/brand/haylending_logo.png" alt="H&Y Lending Corp" style={{ height: '32px', width: 'auto' }} />
                </Box>
            </Toolbar>
            <List>
                {menuItems.map(item => (
                    <ListItem key={item.text} disablePadding>
                        <ListItemButton onClick={() => handleNavigation(item.path)}>
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.text} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </div>
    )

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                        <img src="/brand/symbol.png" alt="H&Y Lending" style={{ height: '28px', marginRight: '12px' }} />
                        <Typography variant="h6" noWrap component="div">
                            H&Y Lending Corporate Portal
                        </Typography>
                    </Box>
                    <Button color="inherit" onClick={handleUserMenuOpen} startIcon={<AccountCircleIcon />}>
                        {user?.name}
                    </Button>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleUserMenuClose}>
                        <MenuItem onClick={handleChangePassword}>Change Password</MenuItem>
                        <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
                <Toolbar />
                {children}
            </Box>
        </Box>
    )
}
